/**
 * File: src/ui-component/notifications/NotificationBell.js
 * Layer: Shared UI
 * Description: Renders the authenticated principal notification bell, inbox
 * popover, realtime socket updates, read status, and notification navigation.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { alpha, useTheme } from '@mui/material/styles';
import {
    Alert,
    Avatar,
    Badge,
    Box,
    CircularProgress,
    ClickAwayListener,
    Divider,
    List,
    ListItemButton,
    Paper,
    Popper,
    Stack,
    Tooltip,
    Typography
} from '@mui/material';
import { IconBellRinging } from '@tabler/icons-react';
import { enqueueSnackbar } from 'notistack';
import moment from 'moment';
import PerfectScrollbar from 'react-perfect-scrollbar';

import { getMyNotifications, markNotificationRead } from 'api/notifications';
import useAuth from 'hooks/useAuth';
import useConfig from 'hooks/useConfig';
import MainCard from 'ui-component/cards/MainCard';
import Transitions from 'ui-component/extended/Transitions';
import { releaseAppSocket, retainAppSocket } from 'utils/app-socket';
const ADDITIONAL_EVIDENCE_REQUEST_TYPE = 'ASSESSMENT.REQUEST-ADDITIONAL-EVIDENCE';
const ASSESSMENT_STARTED_TYPE = 'ASSESSMENT.STARTED';
const ASSESSMENT_SUBMITTED_TYPE = 'ASSESSMENT.SUBMITTED';
const GUEST_INVITATION_ACCEPTED_TYPE = 'GUEST.INVITATION_ACCEPTED';
const GUEST_INVITATION_DECLINED_TYPE = 'GUEST.INVITATION_DECLINED';
const GUEST_EVIDENCE_SUBMITTED_TYPE = 'GUEST.EVIDENCE_SUBMITTED';

const styles = {
    triggerWrap: {
        ml: 2,
        mr: 1
    },
    triggerBadge: (theme) => ({
        '& .MuiBadge-badge': {
            right: 4,
            top: 4,
            minWidth: theme.portal ? 22 : 18,
            height: theme.portal ? 22 : 18,
            px: 0.5,
            fontSize: theme.portal?.minimumFontSize || '0.65rem',
            fontWeight: 700
        }
    }),
    panelCard: {
        width: 420,
        maxWidth: 'calc(100vw - 24px)'
    },
    panelHeader: {
        p: 2,
        pb: 1.5
    },
    panelHeaderRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1
    },
    panelBody: {
        maxHeight: 'calc(100vh - 220px)',
        overflowX: 'hidden'
    },
    list: {
        p: 0
    },
    listItem: (theme, borderRadius, unread, disabled) => ({
        alignItems: 'flex-start',
        gap: 1.5,
        px: 2,
        py: 1.5,
        borderRadius: 0,
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor: unread ? alpha(theme.palette.secondary.main, 0.08) : theme.palette.background.paper,
        opacity: disabled ? 0.7 : 1,
        '&:hover': {
            backgroundColor: unread ? alpha(theme.palette.secondary.main, 0.14) : alpha(theme.palette.primary.main, 0.05)
        },
        '&:last-of-type': {
            borderBottom: 'none',
            borderBottomLeftRadius: `${borderRadius}px`,
            borderBottomRightRadius: `${borderRadius}px`
        }
    }),
    itemIcon: (theme, unread) => ({
        mt: 0.25,
        minWidth: 36,
        width: 36,
        height: 36,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: unread ? theme.palette.secondary.main : theme.palette.text.secondary,
        backgroundColor: unread ? alpha(theme.palette.secondary.main, 0.14) : alpha(theme.palette.text.secondary, 0.08)
    }),
    itemContent: {
        minWidth: 0,
        flex: 1
    },
    itemHeader: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 1
    },
    itemTitleWrap: {
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        minWidth: 0
    },
    itemTitle: {
        lineHeight: 1.3
    },
    unreadDot: (theme) => ({
        width: 8,
        height: 8,
        borderRadius: '50%',
        flexShrink: 0,
        backgroundColor: theme.palette.secondary.main
    }),
    itemTime: {
        flexShrink: 0,
        whiteSpace: 'nowrap',
        color: 'text.secondary'
    },
    itemMessage: {
        mt: 0.5,
        color: 'text.secondary',
        display: '-webkit-box',
        overflow: 'hidden',
        WebkitLineClamp: 3,
        WebkitBoxOrient: 'vertical'
    },
    itemMeta: {
        mt: 0.75,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1
    },
    unreadLabel: (theme) => ({
        color: theme.palette.secondary.main,
        fontWeight: 600
    }),
    clickableHint: {
        textDecoration: 'underline'
    },
    emptyState: {
        px: 2,
        py: 4,
        textAlign: 'center'
    },
    loadingWrap: {
        py: 4,
        alignItems: 'center'
    },
    errorWrap: {
        p: 2
    }
};

const formatNotificationDate = (value) => {
    if (!value) return '';

    const date = moment(value);
    if (!date.isValid()) return '';
    if (Math.abs(moment().diff(date, 'days')) < 7) {
        return date.fromNow();
    }

    return date.format('MMM DD, YYYY h:mm A');
};

const isUnreadNotification = (notification = {}) => notification?.status !== 'read';

const buildAdditionalEvidenceRequestLink = (notification = {}) => {
    const assessmentCode = String(notification?.assessmentCode || '').trim();
    const explicitLink = String(notification?.link || '').trim();
    const metadata = notification?.metadata || {};
    const unitCode = String(metadata.unitCode || '').trim();
    const outcomeId = String(metadata.outcomeId || '').trim();
    const requestId = String(metadata.newRequestId || metadata.requestId || '').trim();

    if (explicitLink) {
        return explicitLink.startsWith('/') ? explicitLink : `/${explicitLink.replace(/^\/+/, '')}`;
    }

    if (!assessmentCode) return '';

    const params = new URLSearchParams();
    if (unitCode) params.set('unitCode', unitCode);
    if (outcomeId) params.set('outcomeId', outcomeId);
    if (requestId) params.set('requestId', requestId);

    const query = params.toString();
    return `/candidate/${assessmentCode}/resubmit-evidence${query ? `?${query}` : ''}`;
};

const buildAssessmentStartedLink = (notification = {}) => {
    const assessmentCode = String(notification?.assessmentCode || notification?.metadata?.assessmentCode || '').trim();
    return assessmentCode ? `/assessments/info/${assessmentCode}` : '';
};

const buildGuestEvidenceLink = (notification = {}) => {
    const assessmentCode = String(notification?.assessmentCode || notification?.metadata?.assessmentCode || '').trim();
    return assessmentCode ? `/candidate/${assessmentCode}/start#guest-evidence` : '';
};

const buildGuestEvidenceSubmittedLink = (notification = {}) => {
    const link = String(notification?.link || '').trim();
    if (link) return link.startsWith('/') ? link : `/${link.replace(/^\/+/, '')}`;

    const assessmentCode = String(notification?.assessmentCode || notification?.metadata?.assessmentCode || '').trim();
    if (!assessmentCode) return '';

    if (notification?.recipientPrincipalType === 'user' || notification?.metadata?.recipientKey === 'assessor') {
        const params = new URLSearchParams();
        const unitCode = String(notification?.metadata?.unitCode || '').trim();
        const outcomeId = String(notification?.metadata?.outcomeId || '').trim();
        if (unitCode) params.set('unitCode', unitCode);
        if (outcomeId) params.set('outcomeId', outcomeId);
        params.set('tab', 'GUEST_LIST');
        return `/assessments/info/${assessmentCode}?${params.toString()}`;
    }

    return buildGuestEvidenceLink(notification);
};

const resolveNotificationLink = (notification = {}) => {
    if (notification?.type === ADDITIONAL_EVIDENCE_REQUEST_TYPE) {
        return buildAdditionalEvidenceRequestLink(notification);
    }

    if (notification?.type === ASSESSMENT_STARTED_TYPE || notification?.type === ASSESSMENT_SUBMITTED_TYPE) {
        return notification?.link || buildAssessmentStartedLink(notification);
    }

    if (notification?.type === GUEST_INVITATION_ACCEPTED_TYPE || notification?.type === GUEST_INVITATION_DECLINED_TYPE) {
        return notification?.link || buildGuestEvidenceLink(notification);
    }

    if (notification?.type === GUEST_EVIDENCE_SUBMITTED_TYPE) {
        return buildGuestEvidenceSubmittedLink(notification);
    }

    const link = String(notification?.link || '').trim();
    if (!link) return '';
    return link.startsWith('/') ? link : `/${link.replace(/^\/+/, '')}`;
};

const getNotificationMetaLabel = (notification = {}) => {
    if (notification.assessmentCode) return `Assessment: ${notification.assessmentCode}`;
    if (notification.senderName) return `From: ${notification.senderName}`;
    if (notification.category) {
        return `${notification.category.charAt(0).toUpperCase()}${notification.category.slice(1)} notification`;
    }

    return 'General notification';
};

export default function NotificationBell({ allowedPrincipalTypes = ['user', 'candidate'] }) {
    const theme = useTheme();
    const navigate = useNavigate();
    const { borderRadius } = useConfig();
    const { isLoggedIn, user } = useAuth();
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [notifications, setNotifications] = useState([]);
    const [updatingIds, setUpdatingIds] = useState([]);
    const anchorRef = useRef(null);
    const socketRef = useRef(null);

    const canShow = isLoggedIn && allowedPrincipalTypes.includes(user?.principalType || 'user');
    const unreadCount = useMemo(() => notifications.filter((notification) => isUnreadNotification(notification)).length, [notifications]);

    const upsertNotification = useCallback((notification) => {
        setNotifications((current) => {
            const existingIndex = current.findIndex((item) => item._id === notification._id);
            if (existingIndex < 0) {
                return [notification, ...current];
            }

            const next = [...current];
            next[existingIndex] = {
                ...next[existingIndex],
                ...notification
            };

            return next;
        });
    }, []);

    const updateNotificationStatus = useCallback((id, data = {}) => {
        setNotifications((current) =>
            current.map((item) =>
                item._id === id
                    ? {
                          ...item,
                          ...data
                      }
                    : item
            )
        );
    }, []);

    const loadNotifications = useCallback(async () => {
        if (!canShow) return;

        setLoading(true);
        setError('');

        try {
            const response = await getMyNotifications({ limit: 50 });
            setNotifications(response.data || []);
        } catch (loadError) {
            const message = loadError?.message || 'Unable to load notifications.';
            setError(message);
            console.error('Unable to load notifications', loadError);
        } finally {
            setLoading(false);
        }
    }, [canShow]);

    useEffect(() => {
        if (!canShow) return undefined;

        loadNotifications();

        const socket = retainAppSocket();
        socketRef.current = socket;

        const handleSocketConnect = () => {
            loadNotifications();
        };

        const handleNotificationNew = (notification) => {
            upsertNotification(notification);
            enqueueSnackbar(notification?.message || notification?.title || 'New notification');
        };

        const handleNotificationRead = (payload) => {
            if (!payload?._id) return;

            updateNotificationStatus(payload._id, {
                status: 'read',
                readAt: payload.readAt || new Date().toISOString()
            });
        };

        const handleSocketError = (socketError) => {
            console.error('Notification socket error', socketError);
        };

        socket.on('connect', handleSocketConnect);
        socket.on('notification:new', handleNotificationNew);
        socket.on('notification:read', handleNotificationRead);
        socket.on('connect_error', handleSocketError);

        return () => {
            socket.off('connect', handleSocketConnect);
            socket.off('notification:new', handleNotificationNew);
            socket.off('notification:read', handleNotificationRead);
            socket.off('connect_error', handleSocketError);
            socketRef.current = null;
            releaseAppSocket();
        };
    }, [canShow, loadNotifications, updateNotificationStatus, upsertNotification]);

    useEffect(() => {
        if (!open) return;
        loadNotifications();
    }, [open, loadNotifications]);

    const handleClose = (event) => {
        if (anchorRef.current && anchorRef.current.contains(event?.target)) {
            return;
        }

        setOpen(false);
    };

    const handleToggle = () => {
        setOpen((current) => !current);
    };

    const handleNotificationClick = async (notification) => {
        if (!notification?._id || updatingIds.includes(notification._id)) {
            return;
        }

        const destination = resolveNotificationLink(notification);

        if (!isUnreadNotification(notification)) {
            if (destination) {
                setOpen(false);
                navigate(destination);
            }
            return;
        }

        setUpdatingIds((current) => [...current, notification._id]);

        try {
            const response = await markNotificationRead(notification._id);
            updateNotificationStatus(notification._id, {
                ...(response?.data || {}),
                status: 'read',
                readAt: response?.data?.readAt || new Date().toISOString()
            });
        } catch (markError) {
            enqueueSnackbar(markError?.message || 'Unable to update notification', {
                variant: 'error'
            });
        } finally {
            setUpdatingIds((current) => current.filter((id) => id !== notification._id));
        }

        if (destination) {
            setOpen(false);
            navigate(destination);
        }
    };

    if (!canShow) return null;

    return (
        <>
            <Box sx={styles.triggerWrap} ref={anchorRef}>
                <Tooltip title="Notifications">
                    <Badge
                        color="error"
                        overlap="circular"
                        max={99}
                        badgeContent={unreadCount}
                        invisible={!unreadCount}
                        sx={styles.triggerBadge(theme)}
                    >
                        <Avatar
                            variant="rounded"
                            sx={{
                                ...theme.typography.commonAvatar,
                                ...theme.typography.mediumAvatar,
                                border: '1px solid',
                                borderColor: theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.primary.light,
                                background: theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.primary.light,
                                color: theme.palette.primary.dark,
                                transition: 'all .2s ease-in-out',
                                '&[aria-controls="menu-list-grow"],&:hover': {
                                    borderColor: theme.palette.primary.main,
                                    background: theme.palette.primary.main,
                                    color: theme.palette.primary.light
                                }
                            }}
                            aria-controls={open ? 'menu-list-grow' : undefined}
                            aria-haspopup="true"
                            title="Notifications"
                            onClick={handleToggle}
                            color="inherit"
                        >
                            <IconBellRinging />
                        </Avatar>
                    </Badge>
                </Tooltip>
            </Box>

            <Popper
                placement="bottom-end"
                open={open}
                anchorEl={anchorRef.current}
                role={undefined}
                transition
                disablePortal
                modifiers={[
                    {
                        name: 'offset',
                        options: {
                            offset: [0, 14]
                        }
                    }
                ]}
            >
                {({ TransitionProps }) => (
                    <ClickAwayListener onClickAway={handleClose}>
                        <Transitions type="zoom" in={open} {...TransitionProps} sx={{ transformOrigin: 'top right' }}>
                            <Paper>
                                {open && (
                                    <MainCard
                                        border={false}
                                        elevation={16}
                                        content={false}
                                        boxShadow
                                        shadow={theme.shadows[16]}
                                        sx={styles.panelCard}
                                    >
                                        <Box sx={styles.panelHeader}>
                                            <Box sx={styles.panelHeaderRow}>
                                                <Box>
                                                    <Typography variant="h4">Notifications</Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {unreadCount
                                                            ? `${unreadCount} unread notification${unreadCount > 1 ? 's' : ''}`
                                                            : 'All caught up'}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Box>

                                        <Divider />

                                        <PerfectScrollbar style={styles.panelBody}>
                                            {loading ? (
                                                <Stack spacing={1} sx={styles.loadingWrap}>
                                                    <CircularProgress size={24} color="secondary" />
                                                    <Typography variant="caption" color="text.secondary">
                                                        Loading notifications...
                                                    </Typography>
                                                </Stack>
                                            ) : error ? (
                                                <Box sx={styles.errorWrap}>
                                                    <Alert severity="error">{error}</Alert>
                                                </Box>
                                            ) : !notifications.length ? (
                                                <Box sx={styles.emptyState}>
                                                    <Typography variant="subtitle1" className="main-font-b">
                                                        No notifications yet
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Assessment updates and requests will appear here.
                                                    </Typography>
                                                </Box>
                                            ) : (
                                                <List sx={styles.list}>
                                                    {notifications.map((notification) => {
                                                        const unread = isUnreadNotification(notification);
                                                        const isUpdating = updatingIds.includes(notification._id);
                                                        const destination = resolveNotificationLink(notification);

                                                        return (
                                                            <ListItemButton
                                                                key={notification._id}
                                                                onClick={() => handleNotificationClick(notification)}
                                                                disabled={isUpdating}
                                                                sx={styles.listItem(theme, borderRadius, unread, isUpdating)}
                                                            >
                                                                <Box sx={styles.itemIcon(theme, unread)}>
                                                                    <IconBellRinging size={18} stroke={1.8} />
                                                                </Box>

                                                                <Box sx={styles.itemContent}>
                                                                    <Box sx={styles.itemHeader}>
                                                                        <Box sx={styles.itemTitleWrap}>
                                                                            <Typography variant="subtitle2" sx={styles.itemTitle}>
                                                                                {notification.title || 'Notification'}
                                                                            </Typography>
                                                                            {unread && (
                                                                                <Box component="span" sx={styles.unreadDot(theme)} />
                                                                            )}
                                                                        </Box>
                                                                        <Typography variant="caption" sx={styles.itemTime}>
                                                                            {formatNotificationDate(notification.createdAt)}
                                                                        </Typography>
                                                                    </Box>

                                                                    <Typography variant="body2" sx={styles.itemMessage}>
                                                                        {notification.message || 'No message provided.'}
                                                                    </Typography>

                                                                    <Box sx={styles.itemMeta}>
                                                                        <Typography
                                                                            variant="caption"
                                                                            color="text.secondary"
                                                                            sx={destination ? styles.clickableHint : undefined}
                                                                        >
                                                                            {getNotificationMetaLabel(notification)}
                                                                        </Typography>
                                                                        {isUpdating ? (
                                                                            <Typography variant="caption" color="text.secondary">
                                                                                Updating...
                                                                            </Typography>
                                                                        ) : unread ? (
                                                                            <Typography variant="caption" sx={styles.unreadLabel(theme)}>
                                                                                Unread
                                                                            </Typography>
                                                                        ) : (
                                                                            <Typography variant="caption" color="text.secondary">
                                                                                Read
                                                                            </Typography>
                                                                        )}
                                                                    </Box>
                                                                </Box>
                                                            </ListItemButton>
                                                        );
                                                    })}
                                                </List>
                                            )}
                                        </PerfectScrollbar>
                                    </MainCard>
                                )}
                            </Paper>
                        </Transitions>
                    </ClickAwayListener>
                )}
            </Popper>
        </>
    );
}
